/**
 * @fileoverview Present the assignor's net-amount and verifier decision, separate from evidence collection in `VerificationWorkflow.js`. The form submits an exact amount and offers only case attorneys above $25,000 or with no amount; `kc-internal-backend/services/payment_verification_assignment.py` re-checks both and owns assignment, Undo and revocation rights. After the 10-second Undo deadline this view polls delivery status every three seconds without replacing the current content, except while the backend reports delivery held (`delivery_blocked`); the backend delivers the Trello card and Slack notice. Anyone who can open the result sees the verifier and read-only details.
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { AppButton } from '../shared';
import './VerificationWorkflow.css';

const ACTIVE = ['pending', 'creating', 'uncertain', 'created'];
export default function VerificationAssignment({ reviewId, result, onUpdated, onReload, onDetails, onHistory }) {
  const workflow = result.workflow || {};
  const assignment = workflow.assignment || result.assignment;
  const assigned = assignment && ACTIVE.includes(assignment.status);
  const [amount, setAmount] = useState(workflow.amount_cents != null ? String(workflow.amount_cents / 100) : '');
  const [employees, setEmployees] = useState([]);
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const operation = useRef(null);
  const active = useRef(true);
  const base = `/payment-instructions/${encodeURIComponent(reviewId)}`;
  const remaining = assignment?.status === 'pending' ? Math.max(0, Math.ceil((Date.parse(assignment.not_before) - now) / 1000)) : 0;
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => {
    let live = true;
    if (workflow.can_assign && !assigned && !result.banking?.confirmed) {
      api.get(base + '/delegate').then(({data}) => { if (live) setEmployees(data.employees); })
        .catch(() => { if (live) setError('Verifiers could not be loaded.'); });
    }
    return () => { live = false; };
  }, [base, workflow.can_assign, assigned, result.banking?.confirmed]);
  useEffect(() => {
    if (assignment?.status !== 'pending') return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [assignment]);
  useEffect(() => {
    if (assignment?.delivery_blocked) return undefined;
    if (!['creating', 'uncertain'].includes(assignment?.status) && !(assignment?.status === 'pending' && remaining === 0)) return undefined;
    // Delivery can remain pending after the deadline; never reload on mount.
    const timer = setInterval(() => onReload?.(), 3000);
    return () => clearInterval(timer);
  }, [assignment?.id, assignment?.status, assignment?.delivery_blocked, remaining, onReload]);
  async function act(action) {
    setBusy(true); setError('');
    try {
      let response;
      if (action === 'assign') {
        const body = { employee_id: Number(assignee), revision: result.revision, amount_kind: 'exact', amount };
        const key = JSON.stringify(body);
        if (operation.current?.key !== key) operation.current = {key, id: crypto.randomUUID()};
        response = await api.post(base + '/delegate', {...body, operation_id: operation.current.id});
      } else response = await api.post(base + `/assignment/${action}`, {assignment_id: assignment.id});
      if (active.current) { onUpdated(response.data); if (action !== 'assign') operation.current = null; }
    } catch (failure) { if (active.current) setError(failure.response?.data?.error || 'Assignment could not be updated.'); }
    finally { if (active.current) setBusy(false); }
  }
  const attorneyRequired = !amount || Number(amount) > 25000;
  return <section className="verification-assignment" aria-label="Assign final verification">
    {result.banking?.confirmed ? <p role="status">Verification complete · {result.banking.recorded_by}</p>
      : assigned ? <>
        <div className="verification-assignment__summary">
          <p><span>Assigned to</span><strong>{assignment.employee_name}</strong></p>
          <p className="verification-assignment__amount"><span>Client to receive</span><strong>{workflow.amount_label}</strong></p>
        </div>
        {['pending','creating','uncertain'].includes(assignment.status) ? <div className="verification-workflow__confirmation" role="status">
          <span>{remaining ? 'Assignment pending' : assignment.delivery_blocked ? 'Task delivery is paused in this environment.' : assignment.last_error ? 'Task creation needs attention.' : 'Creating verification task…'}</span>
          {workflow.can_undo && remaining > 0 ? <AppButton variant="secondary" disabled={busy} onClick={() => act('undo')}>Undo · {remaining}s</AppButton> : null}
        </div> : null}
      </> : workflow.can_assign ? <>
        <h3>Assign final verification</h3>
        <div className="verification-assignment__fields">
        <label>Amount going to client ($)<input aria-label="Amount going to client ($)" inputMode="decimal" value={amount} disabled={busy} onChange={e => setAmount(e.target.value)} /></label>
        <label>Assign verification to<select aria-label="Assign verification to" value={assignee} disabled={busy} onChange={e => setAssignee(e.target.value)}>
          <option value="">Choose verifier</option>{employees.filter(e => !attorneyRequired || e.case_attorney).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select></label>
        </div>
      </> : <p>A case attorney or administrator needs to assign final verification.</p>}
    <div className="verification-assignment__footer">
      <div className="verification-assignment__links">
        <AppButton variant="secondary" icon="pi pi-eye" onClick={onDetails}>View submitted details</AppButton>
        <AppButton variant="secondary" icon="pi pi-history" onClick={onHistory}>View history</AppButton>
      </div>
      {!result.banking?.confirmed && !assigned && workflow.can_assign ? <AppButton disabled={busy || !/^\d+(\.\d{1,2})?$/.test(amount) || !employees.some(e => String(e.id) === assignee && (!attorneyRequired || e.case_attorney))} onClick={() => act('assign')}>Assign verification</AppButton> : null}
      {assigned && workflow.can_revoke_assignment ? <AppButton variant="secondary" disabled={busy} onClick={() => act('revoke')}>Revoke assignment</AppButton> : null}
    </div>
    {error ? <p role="alert">{error}</p> : null}
  </section>;
}
